import { useReduxState } from 'hooks';
import { RichTextValue } from 'components/common';
import { ReviewStars } from 'components/common/tasks';
import { CalendarEventFeedbackQuestionOption } from '.';

import type { CalendarEventFeedbackQuestion } from 'shared-types';
import type { GetCalendarEventFeedbackAnalyticsRes } from 'shared-types/responses';

interface Props {
  analytics: GetCalendarEventFeedbackAnalyticsRes['analyticsByQuestionId'][number];
  question: CalendarEventFeedbackQuestion;
}

/**
 * The metrics for a single feedback question, laid out to match its type and
 * presented in the same card the form itself uses for a question, so the two
 * views of one question look like the same thing.
 *
 * These always cover every submission for the event - they are not affected
 * by the search or filters applied to the answers table below them.
 *
 * The counts sit on their own line, with the metrics that vary by type on the
 * next one.
 */
const CalendarEventFeedbackQuestionAnalytics = ({
  analytics,
  question
}: Props) => {
  const { messages, theme } = useReduxState();

  const questionTypeLabels = {
    multiplechoice: messages.eventCalendarFeedbackTypeMultipleChoice,
    openquestion: messages.eventCalendarFeedbackTypeOpenQuestion,
    singlechoice: messages.eventCalendarFeedbackTypeSingleChoice,
    starrating: messages.eventCalendarFeedbackTypeStarRating
  };

  return (
    <div className="analytics">
      <div className="analytics-meta">
        {questionTypeLabels[question.type]}

        {question.isRequired ? null : (
          <>
            <span style={{ padding: '0 10px' }}>{'•'}</span>
            <span>{messages.commonOptional}</span>
          </>
        )}
      </div>

      <div className="analytics-question">
        <RichTextValue html={question.question} />
      </div>

      {(analytics.type === 'singlechoice' ||
        analytics.type === 'multiplechoice') &&
      (question.type === 'singlechoice' ||
        question.type === 'multiplechoice') ? (
        <div className="analytics-options">
          {question.data.options.map((option) => {
            const optionFrequency = analytics.options.find(
              (item) => item.optionId === option.id
            );

            return (
              <CalendarEventFeedbackQuestionOption
                key={option.id}
                numberOfAnsweredSubmissions={analytics.numberOfAnswers}
                numberOfAnswers={optionFrequency?.numberOfAnswers || 0}
              >
                {option.text}
              </CalendarEventFeedbackQuestionOption>
            );
          })}
        </div>
      ) : null}

      {analytics.type === 'starrating' && question.type === 'starrating' ? (
        <div className="starrating">
          <div className="starrating-average">
            <div className="starrating-average-label">
              {messages.eventCalendarFeedbackAverageRating}
              {':'}
            </div>

            <div className="starrating-average-value">
              {analytics.averageRating}
            </div>

            <div className="starrating-average-stars">
              <ReviewStars
                numberOfStars={question.data.numberOfStars}
                value={analytics.averageRating}
              />
            </div>
          </div>

          <div className="starrating-options">
            {analytics.options.map((option) => (
              <CalendarEventFeedbackQuestionOption
                key={option.rating}
                numberOfAnsweredSubmissions={analytics.numberOfAnswers}
                numberOfAnswers={option.numberOfAnswers || 0}
              >
                <div className="starrating-option-stars">
                  <ReviewStars
                    numberOfStars={question.data.numberOfStars}
                    value={option.rating}
                  />
                </div>
              </CalendarEventFeedbackQuestionOption>
            ))}
          </div>
        </div>
      ) : null}

      <style jsx>{`
        .analytics {
          padding: 5px;
        }
        .analytics-meta {
          color: ${theme.textColorLighter};
          font-size: 1.4rem;
          font-weight: 600;
          margin-bottom: 15px;
        }
        .analytics-question {
          color: ${theme.textColor};
          font-size: 1.6rem;
          margin-bottom: 15px;
        }
        .analytics-options,
        .starrating {
          margin-top: 30px;
        }
        .starrating-average {
          align-items: center;
          background-color: ${theme.lightGreyLighter};
          border-radius: ${theme.cardBorderRadius};
          display: flex;
          gap: 10px;
          padding: 10px 15px;
        }
        .starrating-average-label {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-weight: 600;
          margin-right: 20px;
        }
        .starrating-average-value {
          color: ${theme.textColorLight};
          font-size: 1.8rem;
          font-weight: 700;
        }
        .starrating-average-stars {
          color: ${theme.textColorLight};
          font-size: 1.3rem;
          position: relative;
          top: 1px;
        }
        .starrating-options {
          margin-top: 10px;
        }
        .starrating-option-stars {
          color: ${theme.textColorLight};
          font-size: 1.2rem;
          padding: 1px 0;
        }
      `}</style>
    </div>
  );
};

export default CalendarEventFeedbackQuestionAnalytics;
